import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Profile } from "../../../types/types";
import { formatWordArray } from "../../../utils/common/formatUtils";
import FightScores from "./fightScores";

interface FighterDetailsProps {
  member: Profile;
}

const cardClass =
  "bg-white rounded-3xl p-4 shadow-[0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06),0_2px_4px_rgba(0,0,0,0.04)]";

const humanize = (key: string) => {
  const s = key.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const Item = ({
  label,
  value,
  full = false,
}: {
  label: string;
  value?: string | null;
  full?: boolean;
}) => {
  const { t } = useTranslation("publicProfile");
  return (
    <div className={`flex flex-col gap-0.5 min-w-0 ${full ? "col-span-2" : ""}`}>
      <span className="text-sm font-medium text-gray-500">
        {t(label, { defaultValue: humanize(label) })}
      </span>
      <span className="text-lg font-medium text-gray-900 leading-7 break-words">
        {value && String(value).trim() ? value : "-"}
      </span>
    </div>
  );
};

const FighterDetails: React.FC<FighterDetailsProps> = ({ member }) => {
  const { t } = useTranslation("publicProfile");
  const [showScores, setShowScores] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showScores) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setShowScores(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showScores]);

  if (!member.fighter) return null;
  const f = member.fighter;
  const sc: any = f.fighterScoreResponseDto;
  const sum = (k: string) => (sc?.amateur?.[k] ?? 0) + (sc?.professional?.[k] ?? 0);

  return (
    <div className={`${cardClass} h-full text-start relative`}>
      <div className="text-sm font-medium text-gray-900 mb-4">
        {t("fighter_details")}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Item label="nickname" value={f.nickname} />
        <Item
          label="date_of_birth"
          value={
            f.dateOfBirth ? new Date(f.dateOfBirth).toLocaleDateString() : "N/A"
          }
        />
        <Item label="stance" value={f.stance} />
        <Item label="height" value={`${f.height} ${f.heightUnit}`} />
        <Item label="weight" value={`${f.weight} ${f.weightUnit}`} />
        <Item label="reach" value={`${f.reach} ${f.reachUnit}`} />
        <Item label="team" value={f.team} />
        <Item label="gym" value={f.gym} />
        <Item label="title_holder" value={f.titleHolder ? "Yes" : "No"} />
        <Item
          label="weight_classes"
          value={f.weightClasses?.join(", ")}
        />
        <Item
          label="training_disciplines"
          value={formatWordArray(f.trainingDisciplines)}
          full
        />
        <Item
          label="fight_style_description"
          value={f.fightStyleDescription}
          full
        />
      </div>

      <div
        ref={popRef}
        className="mt-6 rounded-2xl border border-gray-200 bg-[#f4f4f5] px-4 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4 relative"
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-gray-500">{t("wins")}</span>
          <span className="text-3xl font-extrabold leading-9 text-green-600">
            {sum("wins")}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-gray-500">
            {t("losses")}
          </span>
          <span className="text-3xl font-extrabold leading-9 text-red-600">
            {sum("losses")}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-gray-500">{t("draw")}</span>
          <span className="text-3xl font-extrabold leading-9 text-gray-900">
            {sum("draw")}
          </span>
        </div>

        {sc && (
          <div>
            <button
              type="button"
              aria-label={t("fight_scores")}
              onClick={() => setShowScores((v) => !v)}
              className="size-9 rounded-3xl bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition-colors"
            >
              <InformationCircleIcon className="w-5 h-5" />
            </button>

            {showScores && (
              <div className="absolute left-0 top-full mt-2 z-30 w-[min(596px,calc(100vw-2.5rem))] max-h-[75vh] overflow-y-auto bg-white rounded-3xl p-6 shadow-[0_14px_28px_rgba(0,0,0,0.12),0_-6px_12px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100">
                <FightScores
                  member={member}
                  onClose={() => setShowScores(false)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FighterDetails;